import React, { useState, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { apiGet, apiPost } from '../../../../src/api/client';
import { SMS_UI_ENABLED } from '../../../../src/config/features';
import Card from '../../../../src/components/Card';
import Button from '../../../../src/components/Button';
import Input from '../../../../src/components/Input';
import FreeUpsellPicker from '../../../../src/components/upsells/FreeUpsellPicker';
import type { UpsellCatalogItem } from '../../../../src/components/upsells/UpsellPolicyPicker';
import Picker from '../../../../src/components/Picker';
import DateTimePicker from '../../../../src/components/DateTimePicker';
import { useToast } from '../../../../src/components/Toast';
import ContentContainer from '../../../../src/components/ContentContainer';
import { useTheme } from '../../../../src/context/ThemeContext';
import type { ColorTheme } from '../../../../src/utils/theme';
import { spacing, fontSize, fontWeight, borderRadius } from '../../../../src/utils/theme';

interface Location { id: string; name: string }
interface WashTier { id: string; name: string; priceInCents: number; displayOrder: number; isActive: boolean; freeVacMinutes?: number | null }
interface RecipientMatch { userId: string; name: string | null; email: string; phone: string | null; accountNumber: string; isMyCustomer: boolean }

type GiftKind = 'free_wash' | 'percent' | 'free_minutes' | 'free_vend' | 'credit' | 'free_upsell';

const CREDIT_PRESETS_CENTS = [1000, 2000, 5000, 10000];

/** YYYY-MM-DD exactly one year from today — the default credit expiration. */
function oneYearOut(): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
}

/**
 * IssueWashCreditScreen ("Issue Wash / Credit") — the ONE owner form for
 * emailed giveaways: free wash, % off, free self-serve minutes (all voucher-
 * backed), or dollar wash credit (owner comp, no Stripe charge). Absorbed the
 * former "Issue Gift % Off" and "Issue Gift Credit" pages (2026-07-22).
 * Accepts ?email= to prefill the recipient and ?kind= to preselect a chip
 * (used by the Customers screen shortcuts).
 */
export default function IssueGiftScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const router = useRouter();
  const toast = useToast();
  const { email: emailParam, kind: kindParam } = useLocalSearchParams<{ email?: string; kind?: string }>();

  const [submitting, setSubmitting] = useState(false);
  const [locations, setLocations] = useState<Location[]>([]);
  const [washTiers, setWashTiers] = useState<WashTier[]>([]);

  const [recipientEmail, setRecipientEmail] = useState(typeof emailParam === 'string' ? emailParam : '');
  // Recipient typeahead: fuzzy over MY customers + exact email/acct#/phone
  // platform-wide. Free-typed emails still work for people with no account.
  const [recipientMatches, setRecipientMatches] = useState<RecipientMatch[]>([]);
  const [pickedRecipient, setPickedRecipient] = useState<RecipientMatch | null>(null);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Mirrors the current input so late-resolving searches can tell they're stale. */
  const recipientInputRef = useRef('');
  const [smsToRecipient, setSmsToRecipient] = useState(false);
  const [giftKind, setGiftKind] = useState<GiftKind>(
    kindParam === 'percent' || kindParam === 'free_minutes' || kindParam === 'free_vend' || kindParam === 'credit' ? kindParam : 'free_wash');
  const [percentOff, setPercentOff] = useState('25');
  // 1.4 R05: percent discounts cover the wash only or wash + add-ons; any wash voucher can give add-ons away free.
  const [discountScope, setDiscountScope] = useState<'wash' | 'wash_and_upsells'>('wash');
  const [freeUpsellIds, setFreeUpsellIds] = useState<string[]>([]);
  const [upsellCatalog, setUpsellCatalog] = useState<UpsellCatalogItem[]>([]);
  // Credit ($) fields — ported from the retired send-credit page.
  const [creditAmountCents, setCreditAmountCents] = useState(2000);
  const [creditCustomAmount, setCreditCustomAmount] = useState('');
  const [creditExpiresOn, setCreditExpiresOn] = useState(oneYearOut());
  // Multi-select applicability (mig 0050 + vacuum/vending axes, mig 0064).
  // Defaults (all tiers checked, self-serve/vacuum/vending unchecked)
  // preserve the old automatic-only behavior.
  // Backend rules enforced client-side:
  //   free_minutes → self-serve and/or vacuums ONLY (no tiers, no vending)
  //   free_wash    → needs ≥1 wash tier; never vacuums or vending
  //   free_vend    → vending only
  //   percent      → any combination
  const [selectedTierIds, setSelectedTierIds] = useState<string[]>([]);
  const [selfServe, setSelfServe] = useState(false);
  const [vacuum, setVacuum] = useState(false);
  const [vending, setVending] = useState(false);
  const [scopeError, setScopeError] = useState<string | null>(null);
  const [freeMinutes, setFreeMinutes] = useState('5');
  // free_minutes scope: self-serve bays and/or vacuums (default self-serve on).
  const [minutesSelfServe, setMinutesSelfServe] = useState(true);
  const [minutesVacuum, setMinutesVacuum] = useState(false);
  // Free vending credit amount in dollars ($0.25–$100 → freeVendCents).
  const [freeVendAmount, setFreeVendAmount] = useState('2');
  const [locationId, setLocationId] = useState<string>('any');
  const [expirationOption, setExpirationOption] = useState('90');
  const [customDays, setCustomDays] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        const locs = await apiGet<Location[]>('/api/locations');
        if (ac.signal.aborted) return;
        setLocations(locs);

        const catalog = await apiGet<WashTier[]>('/api/wash-tiers');
        if (ac.signal.aborted) return;
        const activeTiers = catalog.filter(t => t.isActive).sort((a, b) => a.displayOrder - b.displayOrder);
        setWashTiers(activeTiers);
        setSelectedTierIds(activeTiers.map(t => t.id));
        apiGet<UpsellCatalogItem[]>('/api/upsells').then(list => { if (!ac.signal.aborted) setUpsellCatalog(list.filter(u => u.isActive !== false)); }).catch(() => {});
      } catch (err: any) {
        if (!ac.signal.aborted) toast.show({ type: 'error', title: 'Error', message: err.message });
      }
    })();
    return () => ac.abort();
  }, []);

  const onRecipientInput = (text: string) => {
    setRecipientEmail(text);
    setPickedRecipient(null);
    recipientInputRef.current = text;
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const q = text.trim();
    if (q.length < 2) { setRecipientMatches([]); return; }
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await apiGet<{ rows: RecipientMatch[] }>(`/api/customers/recipient-search?q=${encodeURIComponent(q)}`);
        // A late response for an outdated query (or after a pick changed the
        // input) must not resurface stale suggestions.
        if (recipientInputRef.current === text) setRecipientMatches(res.rows);
      } catch { /* search-as-you-type stays silent */ }
    }, 300);
  };

  const pickRecipient = (m: RecipientMatch) => {
    setPickedRecipient(m);
    setRecipientEmail(m.email);
    recipientInputRef.current = m.email;
    setRecipientMatches([]);
  };

  const getExpirationDays = (): number => {
    switch (expirationOption) {
      case '30': return 30;
      case '60': return 60;
      case '90': return 90;
      case 'custom': return Math.max(1, parseInt(customDays) || 90);
      default: return 90;
    }
  };

  const creditFinalCents = creditCustomAmount
    ? Math.max(100, Math.min(50000, Math.round(parseFloat(creditCustomAmount) * 100) || 0))
    : creditAmountCents;

  const handleSubmit = async () => {
    if (!recipientEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail.trim())) {
      toast.show({ type: 'error', title: 'Error', message: 'Enter a valid email address' });
      return;
    }

    // ── Credit ($): owner comp via wash-credit, not a voucher. ──
    if (giftKind === 'credit') {
      if (creditFinalCents < 100 || creditFinalCents > 50000) {
        toast.show({ type: 'error', title: 'Error', message: 'Amount must be $1–$500' });
        return;
      }
      if (!/^\d{4}-\d{2}-\d{2}$/.test(creditExpiresOn) || new Date(`${creditExpiresOn}T23:59:59Z`).getTime() <= Date.now()) {
        toast.show({ type: 'error', title: 'Error', message: 'Expiration date must be in the future' });
        return;
      }
      try {
        setSubmitting(true);
        // No Stripe charge — backend writes the wash_credits row with
        // sender_paid_amount_cents = 0 and emails the recipient.
        const res = await apiPost<{ autoDeposited: boolean }>('/api/owner/wash-credit/comp', {
          recipientEmail: recipientEmail.trim().toLowerCase(),
          amountCents: creditFinalCents,
          message: message.trim() || undefined,
          expiresOn: creditExpiresOn,
          smsToRecipient: smsToRecipient || undefined,
        });
        if (res.autoDeposited) {
          toast.show({ type: 'success', title: 'Credit deposited', message: `It's in ${recipientEmail}'s Rewards now — they'll get an email too.` });
        } else {
          toast.show({
            type: 'info', title: 'Reserved — no account yet',
            message: `No RinsePay account uses ${recipientEmail}. The credit is reserved and deposits when they sign up with that exact email — double-check it for typos.`,
          });
        }
        router.back();
      } catch (err: any) {
        toast.show({ type: 'error', title: 'Error', message: err.message });
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // ── Voucher-backed kinds (free wash / % off / free minutes / free vend). ──
    if (giftKind === 'percent') {
      const pct = parseInt(percentOff);
      if (!pct || pct < 1 || pct > 99) {
        toast.show({ type: 'error', title: 'Error', message: 'Percent must be 1–99 (use Free wash for 100%)' });
        return;
      }
    }
    if (giftKind === 'free_minutes') {
      const m = parseInt(freeMinutes);
      if (!m || m <= 0 || m > 60) {
        toast.show({ type: 'error', title: 'Error', message: 'Free minutes must be 1–60' });
        return;
      }
      if (!minutesSelfServe && !minutesVacuum) {
        setScopeError('Pick self-serve bays, vacuums, or both.');
        return;
      }
    }
    if (giftKind === 'free_vend') {
      const cents = Math.round((parseFloat(freeVendAmount) || 0) * 100);
      if (cents < 25 || cents > 10000) {
        toast.show({ type: 'error', title: 'Error', message: 'Vending credit must be $0.25–$100' });
        return;
      }
    }
    if (giftKind === 'free_wash' || giftKind === 'percent') {
      if (selectedTierIds.length === 0 && !selfServe && !vacuum && !vending) {
        setScopeError('Select at least one wash type the gift applies to.');
        return;
      }
      // Server rejects free_wash without a wash tier ("Use free_minutes").
      if (giftKind === 'free_wash' && selectedTierIds.length === 0) {
        setScopeError('Free-wash gifts need at least one wash tier — use Free minutes for self-serve/vacuums.');
        return;
      }
    }
    if (expirationOption === 'custom' && (!customDays || parseInt(customDays) <= 0)) {
      toast.show({ type: 'error', title: 'Error', message: 'Enter valid expiration days' });
      return;
    }

    try {
      setSubmitting(true);
      const body: any = {
        kind: 'gift',
        smsToRecipient: smsToRecipient || undefined,
        locationId: locationId === 'any' ? null : locationId,
        recipientEmail: recipientEmail.trim().toLowerCase(),
        expiresInDays: getExpirationDays(),
        message: message.trim() || undefined,
      };
      if (giftKind === 'free_minutes') {
        // appliesTo carries the self-serve/vacuum split (no tiers, no vending).
        body.discountKind = 'free_minutes';
        body.freeMinutes = parseInt(freeMinutes);
        body.appliesTo = { tierIds: [], selfServe: minutesSelfServe, vacuum: minutesVacuum };
      } else if (giftKind === 'free_vend') {
        // Vending-only scope is mandatory server-side for free_vend.
        body.discountKind = 'free_vend';
        body.freeVendCents = Math.round((parseFloat(freeVendAmount) || 0) * 100);
        body.appliesTo = { tierIds: [], selfServe: false, vending: true };
      } else {
        // appliesTo is authoritative; legacy bayType/tierId are dropped.
        // free_wash never includes vacuums/vending (UI disables those boxes).
        body.discountKind = giftKind;
        if (giftKind === 'percent') { body.percentOff = parseInt(percentOff); body.discountScope = discountScope; }
        if (freeUpsellIds.length && giftKind !== 'percent') body.freeUpsellIds = freeUpsellIds;
        if (giftKind === 'free_upsell' && freeUpsellIds.length === 0) { setScopeError('Pick at least one free add-on.'); setSubmitting(false); return; }
        body.appliesTo = {
          tierIds: selectedTierIds,
          selfServe,
          vacuum: giftKind === 'percent' ? vacuum : false,
          vending: giftKind === 'percent' ? vending : false,
        };
      }
      await apiPost('/api/vouchers', body);
      toast.show({ type: 'success', title: 'Gift sent', message: `${recipientEmail} will get an email shortly.` });
      router.back();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const toggleTier = (id: string) => {
    setScopeError(null);
    setSelectedTierIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleSelfServe = () => { setScopeError(null); setSelfServe(v => !v); };
  const toggleVacuum = () => { setScopeError(null); setVacuum(v => !v); };
  const toggleVending = () => { setScopeError(null); setVending(v => !v); };

  const locationOptions = [
    { label: 'Any location', value: 'any' },
    ...locations.map(l => ({ label: l.name, value: l.id })),
  ];
  const expirationOptions = [
    { label: '30 Days', value: '30' },
    { label: '60 Days', value: '60' },
    { label: '90 Days (default)', value: '90' },
    { label: 'Custom', value: 'custom' },
  ];

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <ContentContainer>
        <Card style={styles.formCard}>
          <Text style={styles.sectionTitle}>Recipient</Text>
          <Input
            label="Email — or search name / phone / account #"
            value={recipientEmail}
            onChangeText={onRecipientInput}
            placeholder="friend@example.com or Jane or USR-1234"
            keyboardType="email-address"
            autoCapitalize="none"
            autoCorrect={false}
          />
          {recipientMatches.map(m => (
            <TouchableOpacity key={m.userId} style={styles.matchRow} onPress={() => pickRecipient(m)}>
              <Ionicons name="person-circle-outline" size={22} color={colors.primary} />
              <View style={{ flex: 1 }}>
                <Text style={styles.matchName} numberOfLines={1}>{m.name || m.email}</Text>
                <Text style={styles.matchSub} numberOfLines={1}>
                  {m.email} · {m.accountNumber}{m.isMyCustomer ? '' : ' · not your customer yet'}
                </Text>
              </View>
              <Ionicons name="chevron-forward" size={16} color={colors.textLight} />
            </TouchableOpacity>
          ))}
          {pickedRecipient ? (
            <Text style={styles.hintText}>
              Sending to {pickedRecipient.name || pickedRecipient.email} ({pickedRecipient.accountNumber}) — it lands in their account right away.
            </Text>
          ) : (
            <Text style={styles.hintText}>
              We'll email them a redemption link. They can sign up for a free account when they redeem.
            </Text>
          )}
        </Card>

        <Card style={styles.formCard}>
          <Text style={styles.sectionTitle}>What to send</Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, marginBottom: spacing.md }}>
            {([['free_wash', 'Free wash'], ['percent', 'Percent off'], ['free_upsell', 'Free add-on with a wash'], ['free_minutes', 'Free minutes'], ['free_vend', 'Free vending credit'], ['credit', 'Credit ($)']] as const).map(([val, label]) => (
              <TouchableOpacity
                key={val}
                onPress={() => { setScopeError(null); setGiftKind(val); }}
                style={[styles.chip, giftKind === val && { backgroundColor: colors.primary, borderColor: colors.primary }]}
              >
                <Text style={[styles.chipText, giftKind === val && { color: '#fff' }]}>{label}</Text>
              </TouchableOpacity>
            ))}
          </View>
          {giftKind === 'credit' ? (
            <>
              <Text style={styles.fieldLabel}>Amount</Text>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, marginBottom: spacing.md }}>
                {CREDIT_PRESETS_CENTS.map(amt => (
                  <TouchableOpacity
                    key={amt}
                    onPress={() => { setCreditAmountCents(amt); setCreditCustomAmount(''); }}
                    style={[styles.chip, !creditCustomAmount && creditAmountCents === amt && { backgroundColor: colors.primary, borderColor: colors.primary }]}
                  >
                    <Text style={[styles.chipText, !creditCustomAmount && creditAmountCents === amt && { color: '#fff' }]}>
                      ${(amt / 100).toFixed(0)}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
              <Input
                label="Custom amount ($1–$500)"
                value={creditCustomAmount}
                onChangeText={setCreditCustomAmount}
                placeholder="e.g. 25"
                keyboardType="decimal-pad"
              />
              <Text style={styles.hintText}>
                Free comp from you — no charge. Spendable like cash at your locations.
              </Text>
            </>
          ) : giftKind === 'free_minutes' ? (
            <>
              <Input label="Free minutes" value={freeMinutes} onChangeText={setFreeMinutes} placeholder="5" keyboardType="number-pad" />
              <Text style={styles.fieldLabel}>Applies to</Text>
              <TouchableOpacity style={styles.checkRow} onPress={() => { setScopeError(null); setMinutesSelfServe(v => !v); }}>
                <View style={[styles.checkbox, minutesSelfServe && styles.checkboxActive]}>
                  {minutesSelfServe && <Text style={styles.checkmark}>✓</Text>}
                </View>
                <Text style={styles.checkLabel}>Self-serve bays</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.checkRow} onPress={() => { setScopeError(null); setMinutesVacuum(v => !v); }}>
                <View style={[styles.checkbox, minutesVacuum && styles.checkboxActive]}>
                  {minutesVacuum && <Text style={styles.checkmark}>✓</Text>}
                </View>
                <Text style={styles.checkLabel}>Vacuums</Text>
              </TouchableOpacity>
              <Text style={styles.hintText}>Free minutes work at self-serve bays and vacuums only — wash tiers and vending aren't eligible.</Text>
              {scopeError && <Text style={styles.errorText}>{scopeError}</Text>}
            </>
          ) : giftKind === 'free_vend' ? (
            <>
              <Input
                label="Vending credit ($0.25–$100)"
                value={freeVendAmount}
                onChangeText={setFreeVendAmount}
                placeholder="2"
                keyboardType="decimal-pad"
              />
              <Text style={styles.hintText}>
                Redeemable at your vending machines only — the credit is pushed to the machine like coins.
              </Text>
            </>
          ) : (
            <>
              {giftKind === 'percent' && (
                <>
                  <Input label="Percent off (1–99)" value={percentOff} onChangeText={setPercentOff} placeholder="25" keyboardType="number-pad" />
                  <Text style={styles.fieldLabel}>Discount covers</Text>
                  <View style={{ flexDirection: 'row', gap: spacing.sm, marginBottom: spacing.sm }}>
                    {([['wash', 'The wash only'], ['wash_and_upsells', 'The wash and upsells']] as const).map(([v, label]) => (
                      <TouchableOpacity key={v} onPress={() => setDiscountScope(v)} style={[styles.chip, discountScope === v && { backgroundColor: colors.primary, borderColor: colors.primary }]}>
                        <Text style={[styles.chipText, discountScope === v && { color: '#fff' }]}>{label}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                </>
              )}
              {giftKind === 'free_upsell' && <Text style={styles.hintText}>The customer pays for the wash and gets the add-ons below free. Pick which wash tiers it works on.</Text>}
              {giftKind !== 'percent' && <FreeUpsellPicker catalog={upsellCatalog} selected={freeUpsellIds} onChange={ids => { setScopeError(null); setFreeUpsellIds(ids); }} title={giftKind === 'free_upsell' ? 'Free upsells' : 'Also free with this wash (optional)'} />}
              <Text style={styles.fieldLabel}>{giftKind === 'free_upsell' ? 'Works with these wash tiers' : 'Applies to'}</Text>
              {washTiers.length === 0 && (
                <Text style={styles.hintText}>No automatic wash tiers found. Add tiers to a bay first.</Text>
              )}
              {washTiers.map(t => (
                <TouchableOpacity key={t.id} style={styles.checkRow} onPress={() => toggleTier(t.id)}>
                  <View style={[styles.checkbox, selectedTierIds.includes(t.id) && styles.checkboxActive]}>
                    {selectedTierIds.includes(t.id) && <Text style={styles.checkmark}>✓</Text>}
                  </View>
                  <Text style={styles.checkLabel}>{t.name} (${(t.priceInCents / 100).toFixed(2)})</Text>
                </TouchableOpacity>
              ))}
              <TouchableOpacity style={styles.checkRow} onPress={toggleSelfServe}>
                <View style={[styles.checkbox, selfServe && styles.checkboxActive]}>
                  {selfServe && <Text style={styles.checkmark}>✓</Text>}
                </View>
                <Text style={styles.checkLabel}>Self-serve bays</Text>
              </TouchableOpacity>
              {/* Vacuums + Vending: percent-off only. free_wash can't apply
                  there (backend rejects) — boxes render disabled with a hint. */}
              <TouchableOpacity
                style={[styles.checkRow, giftKind !== 'percent' && { opacity: 0.4 }]}
                onPress={toggleVacuum}
                disabled={giftKind !== 'percent'}
              >
                <View style={[styles.checkbox, giftKind === 'percent' && vacuum && styles.checkboxActive]}>
                  {giftKind === 'percent' && vacuum && <Text style={styles.checkmark}>✓</Text>}
                </View>
                <Text style={styles.checkLabel}>Vacuums</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.checkRow, giftKind !== 'percent' && { opacity: 0.4 }]}
                onPress={toggleVending}
                disabled={giftKind !== 'percent'}
              >
                <View style={[styles.checkbox, giftKind === 'percent' && vending && styles.checkboxActive]}>
                  {giftKind === 'percent' && vending && <Text style={styles.checkmark}>✓</Text>}
                </View>
                <Text style={styles.checkLabel}>Vending machines</Text>
              </TouchableOpacity>
              {giftKind === 'free_wash' && (
                <Text style={styles.hintText}>
                  Free washes apply to wash tiers only — use Free minutes for vacuums or Free vending credit for vending.
                </Text>
              )}
              {giftKind === 'free_wash' && selfServe && selectedTierIds.length === 0 && (
                <Text style={styles.hintText}>Self-serve-only gifts should use Free minutes instead.</Text>
              )}
              {giftKind === 'free_wash' && washTiers.some(t => (t.freeVacMinutes ?? 0) > 0) && (
                <Text style={styles.hintText}>
                  Free-wash gifts also grant the tier's free vacuum minutes when the recipient redeems, if that tier is configured with them.
                </Text>
              )}
              {scopeError && <Text style={styles.errorText}>{scopeError}</Text>}
            </>
          )}
          {giftKind !== 'credit' && (
            <Picker label="Valid at" options={locationOptions} value={locationId} onValueChange={setLocationId} />
          )}
        </Card>

        <Card style={styles.formCard}>
          <Text style={styles.sectionTitle}>Expiration</Text>
          {giftKind === 'credit' ? (
            <>
              <DateTimePicker label="Credit expires on" mode="date" value={creditExpiresOn} onChange={setCreditExpiresOn} />
              <Text style={styles.hintText}>Defaults to 1 year from today. Unused balance expires at the end of this day.</Text>
            </>
          ) : (
            <>
              <Picker label="Expires in" options={expirationOptions} value={expirationOption} onValueChange={setExpirationOption} />
              {expirationOption === 'custom' && (
                <Input label="Days" value={customDays} onChangeText={setCustomDays} placeholder="45" keyboardType="number-pad" />
              )}
            </>
          )}
        </Card>

        <Card style={styles.formCard}>
          <Text style={styles.sectionTitle}>Personal Message (optional)</Text>
          <Input
            value={message}
            onChangeText={setMessage}
            placeholder='e.g. "Hope you enjoy a free wash on us — Sparkle"'
            multiline
            maxLength={1000}
            numberOfLines={3}
          />
        </Card>

        {SMS_UI_ENABLED && (
        <TouchableOpacity
          onPress={() => setSmsToRecipient(v => !v)}
          style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginTop: spacing.md }}
        >
          <Ionicons name={smsToRecipient ? 'checkbox' : 'square-outline'} size={22} color={colors.primary} />
          <View style={{ flex: 1 }}>
            <Text style={{ fontSize: fontSize.sm, color: colors.text }}>Also text the recipient the code</Text>
            <Text style={{ fontSize: fontSize.xs, color: colors.warning }}>
              Additional charge: ~1¢ per text, billed weekly at actual carrier cost. Sent only if they opted in to texts.
            </Text>
          </View>
        </TouchableOpacity>
        )}
        <Button
          title={giftKind === 'credit' ? `Issue $${(creditFinalCents / 100).toFixed(2)} Credit` : 'Send Gift'}
          onPress={handleSubmit} loading={submitting} size="lg" style={{ marginTop: spacing.md }}
        />
        <View style={{ height: spacing.xxl }} />
      </ContentContainer>
    </ScrollView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: spacing.md },
  formCard: { marginBottom: spacing.md },
  sectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, marginBottom: spacing.md },
  hintText: { fontSize: fontSize.sm, color: c.textLight, fontStyle: 'italic', marginTop: spacing.xs },
  fieldLabel: { fontSize: fontSize.sm, fontWeight: fontWeight.medium as any, color: c.textSecondary, marginBottom: spacing.xs },
  chip: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: borderRadius.md, borderWidth: 1, borderColor: c.borderLight },
  chipText: { fontSize: fontSize.sm, color: c.text },
  checkRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: spacing.sm },
  checkbox: { width: 20, height: 20, borderRadius: 4, borderWidth: 2, borderColor: c.border, justifyContent: 'center', alignItems: 'center' },
  checkboxActive: { backgroundColor: c.primary, borderColor: c.primary },
  checkmark: { color: c.white, fontSize: 12, fontWeight: fontWeight.bold as any },
  checkLabel: { fontSize: fontSize.sm, color: c.text, marginLeft: spacing.sm, flexShrink: 1 },
  errorText: { fontSize: fontSize.sm, color: c.danger, marginTop: spacing.xs },
  matchRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.sm, borderBottomWidth: 1, borderBottomColor: c.borderLight },
  matchName: { fontSize: fontSize.sm, fontWeight: fontWeight.medium as any, color: c.text },
  matchSub: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 1 },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
